import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { maskitoParseNumber } from '@maskito/kit';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { PredefinedInputMaskOptions } from '@blaze-shared/shared-ui';
import {
  DEFAULT_NUMBER_PRECISION,
  getCurrencyFormat,
  getCurrencyFormatter,
  isNullish,
  isNullishOrEmptyString,
} from '@blaze-shared/shared-util';

import { type InlineEditorConfig } from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-currency-editor',
  templateUrl: './currency-editor.component.html',
  styleUrls: ['./currency-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableCurrencyEditorComponent implements OnInit, AfterViewInit {
  valueChange = output<string | null>();

  @Input() config?: InlineEditorConfig;

  @ViewChild('stringInput') stringInput!: ElementRef<HTMLInputElement>;

  formControl = new FormControl('');
  maskOptions: PredefinedInputMaskOptions | null = null;
  currencyFormat = '';

  @Input() set value(val: string | number | undefined) {
    if (isNullish(val) || typeof val === 'string') {
      return;
    }
    this.formControl.patchValue(val.toString());
  }

  constructor() {
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (isNullishOrEmptyString(value?.replaceAll(this.currencyFormat, '').trim())) {
        this.emitValue(null);
        return;
      }
      let parsed = maskitoParseNumber(value);
      if (isNaN(parsed)) {
        parsed = 0;
      }
      this.emitValue(parsed.toString());
    });
  }

  ngOnInit(): void {
    const format = getCurrencyFormat(this.config?.columnSchema?.format?.currencySymbol);
    this.currencyFormat =
      getCurrencyFormatter(this.config?.columnSchema?.format?.currencySymbol || '')
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value || '';

    const thousandSeparator =
      new Intl.NumberFormat().formatToParts(1000).find((part) => part.type === 'group')?.value ??
      '';
    const precision: number = Number.isInteger(this.config?.columnSchema?.precision)
      ? (this.config?.columnSchema?.precision ?? DEFAULT_NUMBER_PRECISION)
      : DEFAULT_NUMBER_PRECISION;

    this.maskOptions = {
      numberFormat: {
        format: 'currency',
        thousandSeparator,
        prefix: format.prefix,
        postfix: format.suffix,
        precision,
      },
    };
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.stringInput.nativeElement.focus();
      this.stringInput.nativeElement.select();
    });
  }

  private emitValue(value: string | null): void {
    this.valueChange.emit(value === '' ? null : value);
  }
}
